import { getReferenceString, MedplumClient, resolveId } from '@medplum/core';
import { QuestionnaireResponse, Task } from '@medplum/fhirtypes';
import { ENCOUNTER_TASK_GROUP_IDENTIFIER } from '@bariendo/lib/codings';
import { trimQuestionnaireResponseForSave } from './trimQuestionnaireResponseForSave';

function stripForCreate(response: QuestionnaireResponse): QuestionnaireResponse {
  const { id: _id, meta: _meta, ...rest } = response;
  void _id;
  void _meta;
  return { ...rest, resourceType: 'QuestionnaireResponse' };
}

export type PersistQuestionnaireResponseEntry = { taskId: string; response: QuestionnaireResponse };

/**
 * Persists pending encounter questionnaire answers: update when the response has an id (or the task
 * already points at a QuestionnaireResponse), otherwise create the response and link it on the Task.
 */
export async function persistEncounterQuestionnaireResponses(
  medplum: MedplumClient,
  entries: PersistQuestionnaireResponseEntry[],
  tasksById: Map<string, Task>
): Promise<PromiseSettledResult<QuestionnaireResponse>[]> {
  const results: PromiseSettledResult<QuestionnaireResponse>[] = [];

  for (const { taskId, response } of entries) {
    try {
      const task = tasksById.get(taskId);
      if (!task?.id) {
        throw new Error(`Task not found: ${taskId}`);
      }

      let toPersist = trimQuestionnaireResponseForSave(response);

      const outputRef = task.output?.[0]?.valueReference?.reference;
      if (!toPersist.id && outputRef?.startsWith('QuestionnaireResponse/')) {
        const healedId = resolveId({ reference: outputRef });
        if (healedId) {
          toPersist = { ...toPersist, id: healedId };
        }
      }

      let persisted: QuestionnaireResponse;
      if (toPersist.id) {
        persisted = await medplum.updateResource<QuestionnaireResponse>(toPersist);
      } else {
        // Read the Task before creating the response: if it can't be read, a created response would be
        // left linked to nothing, and an autosave retry would leave another one each time (BAR-3112).
        const latestTask = (await medplum.readResource('Task', task.id)) as Task;
        const created = await medplum.createResource<QuestionnaireResponse>(stripForCreate(toPersist));
        persisted = created;
        await medplum.updateResource({
          ...latestTask,
          groupIdentifier: latestTask.groupIdentifier ?? ENCOUNTER_TASK_GROUP_IDENTIFIER,
          output: [
            {
              type: { text: 'QuestionnaireResponse' },
              valueReference: { reference: getReferenceString(created) },
            },
          ],
        });
      }

      results.push({ status: 'fulfilled', value: persisted });
    } catch (reason) {
      results.push({ status: 'rejected', reason });
    }
  }

  return results;
}
